import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import Toast from '../Toast';
import ConfirmDialog from '../ConfirmDialog';
import LoadingOverlay from '../LoadingOverlay';

const StaffManagement = () => {
    const { user, restaurant } = useAppSelector(state => state.auth);
    const restaurantId = restaurant?._id || user?.id;
    
    const [staff, setStaff] = useState([]);
    const [filteredStaff, setFilteredStaff] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [showModal, setShowModal] = useState(false);
    const [editingStaff, setEditingStaff] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmDialog, setConfirmDialog] = useState({ 
        isOpen: false, 
        title: '', 
        message: '', 
        onConfirm: null,
        confirmText: 'Confirm',
        confirmColor: 'red'
    });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        photoUrl: '',
        govtIdPhotoUrl: '',
        address: '',
        govtIdType: '',
        govtIdNumber: '',
        roleId: '',
        joiningDate: new Date().toISOString().split('T')[0],
        employmentType: 'FULL_TIME'
    });
    const [employeePhotoPreview, setEmployeePhotoPreview] = useState(null);
    const [govtIdPhotoPreview, setGovtIdPhotoPreview] = useState(null);

    useEffect(() => {
        if (restaurantId) {
            fetchStaff();
            fetchRoles();
        }
    }, [restaurantId]);

    const fetchStaff = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_ENDPOINTS.STAFF_ALL(restaurantId));
            setStaff(response.data);
            setFilteredStaff(response.data);
        } catch (error) {
            showToast('Failed to fetch staff', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Filter staff based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredStaff(staff);
        } else {
            const filtered = staff.filter(member => 
                member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.phone?.includes(searchTerm) ||
                member.roleId?.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredStaff(filtered);
        }
    }, [searchTerm, staff]);

    const fetchRoles = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.ROLES_ALL(restaurantId));
            setRoles(response.data);
        } catch (error) {
            console.error('Failed to fetch roles:', error);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileUpload = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            showToast('Please select an image file', 'error');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            showToast('File size should be less than 5MB', 'error');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result;
            if (type === 'employee') {
                setFormData(prev => ({ ...prev, photoUrl: base64String }));
                setEmployeePhotoPreview(base64String);
            } else if (type === 'govtId') {
                setFormData(prev => ({ ...prev, govtIdPhotoUrl: base64String }));
                setGovtIdPhotoPreview(base64String);
            }
        };
        reader.onerror = () => {
            showToast('Failed to read file', 'error');
        };
        reader.readAsDataURL(file);
    };

    const handleRemovePhoto = (type) => {
        if (type === 'employee') {
            setFormData(prev => ({ ...prev, photoUrl: '' }));
            setEmployeePhotoPreview(null);
        } else if (type === 'govtId') {
            setFormData(prev => ({ ...prev, govtIdPhotoUrl: '' }));
            setGovtIdPhotoPreview(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        setLoadingMessage(editingStaff ? 'Updating staff...' : 'Creating staff and sending credentials...');
        try {
            // Prepare form data, ensuring govtIdNumber is properly handled
            const submitData = {
                ...formData,
                restaurantId,
                // Only include govtIdNumber if it's not empty
                govtIdNumber: formData.govtIdNumber && formData.govtIdNumber.trim() !== '' 
                    ? formData.govtIdNumber.trim() 
                    : null,
                // Only include govtIdType if govtIdNumber is provided
                govtIdType: (formData.govtIdNumber && formData.govtIdNumber.trim() !== '') 
                    ? formData.govtIdType 
                    : null
            };

            if (editingStaff) {
                await axios.put(API_ENDPOINTS.STAFF_UPDATE(editingStaff._id), submitData);
                showToast('Staff updated successfully');
            } else {
                const response = await axios.post(API_ENDPOINTS.STAFF_CREATE, submitData);
                const message = response.data?.message || 
                    (response.data?.emailSent 
                        ? 'Staff created successfully. Credentials have been sent to their email.' 
                        : 'Staff created successfully. Please share the credentials manually.');
                showToast(message);
            }
            setShowModal(false);
            setEditingStaff(null);
            resetForm();
            await fetchStaff();
        } catch (error) {
            showToast(error.response?.data?.message || 'Operation failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleEdit = (staffMember) => {
        setEditingStaff(staffMember);
        setFormData({
            name: staffMember.name || '',
            email: staffMember.email || '',
            phone: staffMember.phone || '',
            photoUrl: staffMember.photoUrl || '',
            govtIdPhotoUrl: staffMember.govtIdPhotoUrl || '',
            address: staffMember.address || '',
            govtIdType: staffMember.govtIdType || '',
            govtIdNumber: staffMember.govtIdNumber || '', // Show decrypted ID from backend
            roleId: staffMember.roleId?._id || staffMember.roleId || '',
            joiningDate: staffMember.joiningDate ? new Date(staffMember.joiningDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            employmentType: staffMember.employmentType || 'FULL_TIME'
        });
        setEmployeePhotoPreview(staffMember.photoUrl || null);
        setGovtIdPhotoPreview(staffMember.govtIdPhotoUrl || null);
        setShowModal(true);
    };

    const handleDeactivate = (id, name) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Deactivate Staff',
            message: `Are you sure you want to deactivate ${name}? They will not be able to login until reactivated.`,
            confirmText: 'Deactivate',
            confirmColor: 'orange',
            onConfirm: async () => {
                setActionLoading(true);
                setLoadingMessage('Deactivating staff...');
                try {
                    await axios.delete(API_ENDPOINTS.STAFF_DELETE(id));
                    showToast('Staff deactivated successfully');
                    await fetchStaff();
                } catch (error) {
                    showToast(error.response?.data?.message || 'Failed to deactivate staff', 'error');
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };

    const handleActivate = async (id) => {
        setActionLoading(true);
        setLoadingMessage('Activating staff...');
        try {
            await axios.post(API_ENDPOINTS.STAFF_ACTIVATE(id));
            showToast('Staff activated successfully');
            await fetchStaff();
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to activate staff', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handlePermanentDelete = (id, name) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Permanently Delete Staff',
            message: `Are you sure you want to permanently delete ${name}? This action cannot be undone and all associated data will be lost.`,
            confirmText: 'Delete Permanently',
            confirmColor: 'red',
            onConfirm: async () => {
                setActionLoading(true);
                setLoadingMessage('Deleting staff...');
                try {
                    await axios.delete(API_ENDPOINTS.STAFF_PERMANENT_DELETE(id));
                    showToast('Staff permanently deleted successfully');
                    await fetchStaff();
                } catch (error) {
                    showToast(error.response?.data?.message || 'Failed to delete staff', 'error');
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            photoUrl: '',
            govtIdPhotoUrl: '',
            address: '',
            govtIdType: '',
            govtIdNumber: '',
            roleId: '',
            joiningDate: new Date().toISOString().split('T')[0],
            employmentType: 'FULL_TIME'
        });
        setEmployeePhotoPreview(null);
        setGovtIdPhotoPreview(null);
    };

    return (
        <div className="relative overflow-x-hidden min-w-0">
            {actionLoading && <LoadingOverlay message={loadingMessage} />}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <h2 className="text-base font-bold text-stone-800 truncate">Staff</h2>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={async () => {
                            await fetchStaff();
                            await fetchRoles();
                            showToast('Staff list refreshed successfully');
                        }}
                        disabled={loading}
                        className="inline-flex items-center justify-center p-2 rounded-lg font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-sm size-9"
                        title="Refresh"
                        aria-label="Refresh"
                    >
                        {loading ? (
                            <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span className="text-sm">↻</span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            resetForm();
                            setEditingStaff(null);
                            setShowModal(true);
                        }}
                        className="h-9 px-3 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors whitespace-nowrap"
                    >
                        + Add Staff
                    </button>
                </div>
            </div>

            <div className="mb-4">
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">🔍</span>
                    <input
                        type="text"
                        placeholder="Search by name, email, phone, or role..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-9 py-2 text-xs border border-stone-200 rounded-xl bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-stone-800 placeholder:text-stone-400"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-600"
                        >
                            ×
                        </button>
                    )}
                </div>
                {searchTerm && (
                    <p className="mt-1 text-[10px] text-stone-500">
                        Showing {filteredStaff.length} of {staff.length}
                    </p>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-400 border-t-transparent" />
                </div>
            ) : (
                <>
                    <div className="hidden md:block bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[1000px] text-xs">
                                <thead className="bg-stone-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Name</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Email</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Phone</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Role</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Govt ID</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Status</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStaff.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-3 py-8 text-center text-stone-500">
                                                {searchTerm ? 'No staff matching your search.' : 'No staff members.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredStaff.map((member) => (
                                            <tr key={member._id} className="border-t border-stone-100 hover:bg-stone-50/50">
                                                <td className="px-3 py-2 font-medium text-stone-800">{member.name}</td>
                                                <td className="px-3 py-2 text-stone-600">{member.email}</td>
                                                <td className="px-3 py-2 text-stone-600">{member.phone}</td>
                                                <td className="px-3 py-2">{member.roleId?.name || '—'}</td>
                                                <td className="px-3 py-2">
                                                    {member.govtIdType && member.govtIdNumber ? (
                                                        <span className="text-stone-600">{member.govtIdType}</span>
                                                    ) : (
                                                        <span className="text-stone-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${member.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                                                        {member.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex flex-wrap gap-1.5">
                                                        <button type="button" onClick={() => handleEdit(member)} className="text-orange-600 hover:text-orange-700 font-medium">Edit</button>
                                                        {member.isActive ? (
                                                            <button type="button" onClick={() => handleDeactivate(member._id, member.name)} className="text-amber-600 hover:text-amber-700 font-medium">Deactivate</button>
                                                        ) : (
                                                            <button type="button" onClick={() => handleActivate(member._id)} className="text-emerald-600 hover:text-emerald-700 font-medium">Activate</button>
                                                        )}
                                                        <button type="button" onClick={() => handlePermanentDelete(member._id, member.name)} className="text-red-600 hover:text-red-700 font-medium">Delete</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="md:hidden space-y-3">
                        {filteredStaff.length === 0 ? (
                            <div className="bg-white rounded-xl border border-stone-200 p-4 text-center text-stone-500 text-xs">
                                {searchTerm ? 'No staff matching your search.' : 'No staff members.'}
                            </div>
                        ) : (
                            filteredStaff.map((member) => (
                                <div key={member._id} className="bg-white rounded-xl border border-stone-200 shadow-sm p-3 border-l-4 border-l-orange-400">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-sm font-semibold text-stone-800 truncate">{member.name}</h3>
                                            <p className="text-xs text-stone-600 truncate">{member.email}</p>
                                        </div>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${member.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                                            {member.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <p className="text-xs text-stone-500 mb-2">Role: {member.roleId?.name || '—'}</p>
                                    <div className="flex flex-wrap gap-1.5 pt-2 border-t border-stone-100">
                                        <button type="button" onClick={() => handleEdit(member)} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-orange-500 text-white">Edit</button>
                                        {member.isActive ? (
                                            <button type="button" onClick={() => handleDeactivate(member._id, member.name)} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-amber-100 text-amber-800">Deactivate</button>
                                        ) : (
                                            <button type="button" onClick={() => handleActivate(member._id)} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-emerald-100 text-emerald-800">Activate</button>
                                        )}
                                        <button type="button" onClick={() => handlePermanentDelete(member._id, member.name)} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-red-100 text-red-800">Delete</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            {showModal && (
                <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4" onClick={() => { setShowModal(false); setEditingStaff(null); resetForm(); }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col border border-stone-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-stone-200 bg-stone-50/80 flex-shrink-0">
                            <h3 className="text-base font-bold text-stone-800">{editingStaff ? 'Edit Staff' : 'Add Staff'}</h3>
                            <button type="button" onClick={() => { setShowModal(false); setEditingStaff(null); resetForm(); }} className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-200 text-lg leading-none">×</button>
                        </div>
                        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                            <div className="overflow-y-auto flex-1 p-3 sm:p-4 space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Name *</p>
                                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Email *</p>
                                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} required disabled={!!editingStaff} className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none disabled:bg-stone-100" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Phone *</p>
                                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Role *</p>
                                        <select name="roleId" value={formData.roleId} onChange={handleInputChange} required className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white">
                                            <option value="">Select role</option>
                                            {roles.map(role => <option key={role._id} value={role._id}>{role.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Employment type</p>
                                        <select name="employmentType" value={formData.employmentType} onChange={handleInputChange} className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white">
                                            <option value="FULL_TIME">Full Time</option>
                                            <option value="PART_TIME">Part Time</option>
                                            <option value="CONTRACT">Contract</option>
                                        </select>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Joining date</p>
                                        <input type="date" name="joiningDate" value={formData.joiningDate} onChange={handleInputChange} className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none" />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Employee photo (optional)</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'employee')} className="hidden" id="employee-photo-upload" />
                                        <label htmlFor="employee-photo-upload" className="cursor-pointer px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600">Upload photo</label>
                                        {employeePhotoPreview && (
                                            <div className="relative">
                                                <img src={employeePhotoPreview} alt="Preview" className="w-14 h-14 object-cover rounded-lg border border-stone-200" />
                                                <button type="button" onClick={() => handleRemovePhoto('employee')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">×</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Govt ID photo (optional)</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'govtId')} className="hidden" id="govt-id-photo-upload" />
                                        <label htmlFor="govt-id-photo-upload" className="cursor-pointer px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50">Upload ID</label>
                                        {govtIdPhotoPreview && (
                                            <div className="relative">
                                                <img src={govtIdPhotoPreview} alt="ID preview" className="w-14 h-14 object-cover rounded-lg border border-stone-200" />
                                                <button type="button" onClick={() => handleRemovePhoto('govtId')} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">×</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Govt ID type</p>
                                        <select name="govtIdType" value={formData.govtIdType} onChange={handleInputChange} className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white">
                                            <option value="">Select</option>
                                            <option value="AADHAAR">Aadhaar</option>
                                            <option value="PAN">PAN</option>
                                            <option value="DRIVING_LICENSE">Driving License</option>
                                            <option value="PASSPORT">Passport</option>
                                            <option value="OTHER">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Govt ID number</p>
                                        <input type="text" name="govtIdNumber" value={formData.govtIdNumber} onChange={handleInputChange} className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none" />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Address</p>
                                    <textarea name="address" value={formData.address} onChange={handleInputChange} rows={2} className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none resize-none" />
                                </div>
                            </div>
                            <div className="flex gap-2 px-3 sm:px-4 py-2.5 border-t border-stone-200 bg-white flex-shrink-0">
                                <button type="button" onClick={() => { setShowModal(false); setEditingStaff(null); resetForm(); }} className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50">Cancel</button>
                                <button type="submit" disabled={actionLoading} className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50">
                                    {actionLoading ? 'Saving...' : (editingStaff ? 'Update' : 'Create')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmText={confirmDialog.confirmText}
                confirmColor={confirmDialog.confirmColor}
            />

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default StaffManagement;

